import React, { useState, useEffect, useCallback } from "react";
import { Table, Card, Tag, Select, DatePicker, Input, Button, Space, Typography, Row, Col, Tooltip, Modal } from "antd";
import { SearchOutlined, ReloadOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import "./auditLog.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Flow type labels
const FLOW_TYPE_LABELS = {
  PLATFORM_INIT: "Platform Init",
  USER_REGISTRATION: "User Registration",
  COMPANY_CREATION: "Company Creation",
  USER_MANAGEMENT: "User Management",
  SYSTEM_ONBOARDING: "System Onboarding",
  VIEW_GROUP_MANAGEMENT: "View Group",
  DISPATCH_PERMISSION: "Dispatch Permission",
  DISPATCH_GROUP_MANAGEMENT: "Dispatch Group",
  GROUP_SYSTEM_MANAGEMENT: "Group Systems",
  SYSTEM_OFFBOARDING: "System Offboarding",
  DISPATCH_EVENT: "Dispatch Event",
  SETTLEMENT: "Settlement",
  AUTHENTICATION: "Authentication",
  ENGINE_OPERATION: "Engine Operation",
};

// Flow type colors
const FLOW_TYPE_COLORS = {
  PLATFORM_INIT: "purple",
  USER_REGISTRATION: "blue",
  COMPANY_CREATION: "cyan",
  USER_MANAGEMENT: "geekblue",
  SYSTEM_ONBOARDING: "green",
  VIEW_GROUP_MANAGEMENT: "orange",
  DISPATCH_PERMISSION: "magenta",
  DISPATCH_GROUP_MANAGEMENT: "volcano",
  GROUP_SYSTEM_MANAGEMENT: "gold",
  SYSTEM_OFFBOARDING: "red",
  DISPATCH_EVENT: "lime",
  SETTLEMENT: "cyan",
  AUTHENTICATION: "blue",
  ENGINE_OPERATION: "#722ed1",
};

// Status colors
const STATUS_COLORS = {
  SUCCESS: "success",
  FAILURE: "error",
  PENDING: "processing",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverTimezone, setServerTimezone] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    flowType: "",
    status: "",
    dateRange: null,
    search: "",
  });
  const [detailModal, setDetailModal] = useState({ visible: false, record: null });

  // Fetch server timezone
  useEffect(() => {
    const fetchServerInfo = async () => {
      try {
        const response = await fetch("/api/server-info");
        if (response.ok) {
          const data = await response.json();
          setServerTimezone(data.timezone || "Australia/Brisbane");
        }
      } catch (err) {
        console.error("Failed to fetch server timezone:", err);
      }
    };
    fetchServerInfo();
  }, []);

  // Format time in server timezone
  const formatServerTime = useCallback((isoTime) => {
    if (!isoTime) return "—";
    const date = new Date(isoTime);
    if (serverTimezone) {
      try {
        return date.toLocaleString("en-AU", {
          timeZone: serverTimezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).replace(",", "");
      } catch (e) {
        // Fallback
      }
    }
    return dayjs(isoTime).format("YYYY-MM-DD HH:mm:ss");
  }, [serverTimezone]);

  const fetchLogs = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
      });

      if (filters.flowType) {
        params.append("flowType", filters.flowType);
      }
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.dateRange && filters.dateRange[0]) {
        params.append("startDate", filters.dateRange[0].format("YYYY-MM-DD"));
      }
      if (filters.dateRange && filters.dateRange[1]) {
        params.append("endDate", filters.dateRange[1].format("YYYY-MM-DD"));
      }

      const response = await fetch(`/api/flows/audit-logs?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch audit logs");
      }

      const data = await response.json();
      setLogs(data.data || []);
      setPagination({
        current: data.page || page,
        pageSize: data.limit || pageSize,
        total: data.total || 0,
      });
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs(pagination.current, pageSize);
  }, []);

  useEffect(() => {
    fetchLogs(1, pageSize);
  }, [pageSize]);

  const handleTableChange = (newPagination) => {
    fetchLogs(newPagination.current, newPagination.pageSize);
  };

  const handleSearch = () => {
    fetchLogs(1, pagination.pageSize);
  };

  const handleReset = () => {
    setFilters({
      flowType: "",
      status: "",
      dateRange: null,
      search: "",
    });
    setTimeout(() => fetchLogs(1, pagination.pageSize), 0);
  };

  const showDetail = (record) => {
    setDetailModal({ visible: true, record });
  };

  // Get short timezone label (e.g., "AEST" from "Australia/Brisbane")
  const getTimezoneLabel = () => {
    if (!serverTimezone) return "";
    try {
      const formatter = new Intl.DateTimeFormat("en-AU", {
        timeZone: serverTimezone,
        timeZoneName: "short",
      });
      const parts = formatter.formatToParts(new Date());
      const tzPart = parts.find(p => p.type === "timeZoneName");
      return tzPart ? tzPart.value : serverTimezone.split("/").pop();
    } catch {
      return serverTimezone.split("/").pop();
    }
  };

  const columns = [
    {
      title: `Time${serverTimezone ? ` (${getTimezoneLabel()})` : ""}`,
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (text) => formatServerTime(text),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Flow Type",
      dataIndex: "flow_type",
      key: "flow_type",
      width: 150,
      render: (type) => (
        <Tag color={FLOW_TYPE_COLORS[type] || "default"}>
          {FLOW_TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: "Operation",
      dataIndex: "operation",
      key: "operation",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Actor",
      dataIndex: "actor_name",
      key: "actor_name",
      width: 150,
      render: (actorName, record) => (
        <Tooltip title={`Type: ${record.actor_type || "USER"}${record.actor_id ? ` | ID: ${record.actor_id}` : ""}`}>
          <Text>{actorName || record.actor_id || "-"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Target",
      key: "target",
      width: 180,
      render: (_, record) => (
        <span>
          {record.target_type && (
            <Tag color="blue" style={{ marginRight: 4 }}>
              {record.target_type}
            </Tag>
          )}
          <Text type="secondary">{record.target_id || "-"}</Text>
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Company",
      dataIndex: "company_id",
      key: "company_id",
      width: 100,
      render: (companyId) => companyId || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showDetail(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const flowTypeOptions = [
    { value: "", label: "All Flow Types" },
    ...Object.entries(FLOW_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  return (
    <PageLayout active="auditLog">
      <div className="audit-log-page">
        <div className="audit-log-header">
          <Title level={2} style={{ margin: 0 }}>Audit Log</Title>
          <Text type="secondary">View all platform operation records</Text>
        </div>

        <Card className="audit-log-filters" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: "100%" }}
                value={filters.flowType}
                onChange={(value) => setFilters({ ...filters, flowType: value })}
                options={flowTypeOptions}
                suffixIcon={<FilterOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: "100%" }}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
                options={[
                  { value: "", label: "All Statuses" },
                  { value: "SUCCESS", label: "Success" },
                  { value: "FAILURE", label: "Failure" },
                  { value: "PENDING", label: "Pending" },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                style={{ width: "100%" }}
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Search
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card className="audit-log-table">
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            pagination={makePagination(pageSize, setPageSize, {
              current: pagination.current,
              total: pagination.total,
              showQuickJumper: true,
            })}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        </Card>

        <Modal
          title="Audit Log Detail"
          open={detailModal.visible}
          onCancel={() => setDetailModal({ visible: false, record: null })}
          footer={null}
          width={700}
        >
          {detailModal.record && (
            <div className="audit-detail">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">ID</Text>
                  <div>{detailModal.record.id}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Time</Text>
                  <div>{formatServerTime(detailModal.record.created_at)}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Flow Type</Text>
                  <div>
                    <Tag color={FLOW_TYPE_COLORS[detailModal.record.flow_type] || "default"}>
                      {FLOW_TYPE_LABELS[detailModal.record.flow_type] || detailModal.record.flow_type}
                    </Tag>
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Operation</Text>
                  <div>{detailModal.record.operation}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Actor</Text>
                  <div>{detailModal.record.actor_name || detailModal.record.actor_id || "-"} ({detailModal.record.actor_type || "USER"})</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Target</Text>
                  <div>{detailModal.record.target_type}: {detailModal.record.target_id || "-"}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Status</Text>
                  <div>
                    <Tag color={STATUS_COLORS[detailModal.record.status] || "default"}>
                      {detailModal.record.status}
                    </Tag>
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Company ID</Text>
                  <div>{detailModal.record.company_id || "-"}</div>
                </Col>
                {detailModal.record.error_message && (
                  <Col span={24}>
                    <Text type="secondary">Error Message</Text>
                    <div style={{ color: "#ff4d4f" }}>{detailModal.record.error_message}</div>
                  </Col>
                )}
                {detailModal.record.request_data && (
                  <Col span={24}>
                    <Text type="secondary">Request Data</Text>
                    <pre className="audit-detail-json">
                      {JSON.stringify(JSON.parse(detailModal.record.request_data), null, 2)}
                    </pre>
                  </Col>
                )}
                {detailModal.record.response_data && (
                  <Col span={24}>
                    <Text type="secondary">Response Data</Text>
                    <pre className="audit-detail-json">
                      {JSON.stringify(JSON.parse(detailModal.record.response_data), null, 2)}
                    </pre>
                  </Col>
                )}
                {detailModal.record.changes && (
                  <Col span={24}>
                    <Text type="secondary">Changes</Text>
                    <pre className="audit-detail-json">
                      {JSON.stringify(JSON.parse(detailModal.record.changes), null, 2)}
                    </pre>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Modal>
      </div>
    </PageLayout>
  );
}
